import React, { useEffect, useState } from 'react';
import {
  Box,
  Grid,
  Paper,
  Typography,
  CircularProgress,
  Autocomplete,
  TextField,
} from '@mui/material';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { DataGrid } from '@mui/x-data-grid';
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos';
import ApiService from '../../functions/apiService';
import ButtonTheme from '../../components/ThemeButton/ThemeButton';
import moment from 'moment';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';

const ActionBadge = ({ action }) => {
  const isAdd = String(action).toUpperCase() === 'ADD';
  return (
    <span
      style={{
        padding: '2px 12px',
        borderRadius: '12px',
        fontSize: '12px',
        fontWeight: 600,
        color: isAdd ? '#2E7D32' : '#C62828',
        backgroundColor: isAdd ? '#E8F5E9' : '#FDECEA',
        textTransform: 'capitalize',
      }}
    >
      {isAdd ? 'Add' : 'Revert'}
    </span>
  );
};

const EmployeeHoursMonitor = () => {
  const navigate = useNavigate();
  const [employeeOptions, setEmployeeOptions] = useState([]);
  const [employee, setEmployee] = useState(null);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [rows, setRows] = useState([]);
  const [currentHours, setCurrentHours] = useState([]);
  const [loading, setLoading] = useState(false);

  const loadEmployees = () => {
    ApiService.get('/employeeToSwap')
      .then(({ data }) => {
        const arr = (data || []).map((emp) => ({
          label: `${emp.EA_Rank ? emp.EA_Rank + ' ' : ''}${emp.fullName}`,
          value: emp.Emp_Code,
        }));
        setEmployeeOptions(arr);
      })
      .catch(() => {});
  };

  const fetchData = () => {
    setLoading(true);
    const payload = {
      emp_code: employee?.value ?? null,
      start_date: startDate ? moment(startDate).format('YYYY-MM-DD') : null,
      end_date: endDate ? moment(endDate).format('YYYY-MM-DD') : null,
    };

    ApiService.post('/emp-hours-monitor', payload)
      .then((res) => {
        setLoading(false);
        const list = res?.data || [];
        setRows(list.map((r) => ({ ...r, id: r.id })));
        setCurrentHours(res?.current_hours || []);
      })
      .catch((err) => {
        setLoading(false);
        toast.error(err?.data?.message || 'Failed to fetch data');
      });
  };

  useEffect(() => {
    loadEmployees();
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const columns = [
    {
      field: 'created_at',
      headerName: 'Date/Time',
      flex: 1,
      minWidth: 170,
      renderCell: (params) => (
        <span style={{ color: '#1976d2' }}>
          {params?.value && moment(params.value).isValid()
            ? moment(params.value).format('MM/DD/YYYY hh:mm A')
            : '-'}
        </span>
      ),
    },
    {
      field: 'employee',
      headerName: 'Employee',
      flex: 1,
      minWidth: 180,
      valueGetter: (params) => params?.row?.employee?.fullName || '-',
    },
    {
      field: 'Overtime_List',
      headerName: 'Overtime List',
      flex: 1,
      minWidth: 150,
    },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      minWidth: 120,
      renderCell: (params) => <ActionBadge action={params?.row?.action} />,
    },
    {
      field: 'hours',
      headerName: 'Hours',
      flex: 1,
      minWidth: 130,
      renderCell: (params) => {
        const rawHours = parseFloat(params?.row?.hours || 0);
        const isPositive = rawHours >= 0;
        const listName = params?.row?.Overtime_List;
        const currentForList = currentHours?.[0]?.lists?.[listName];
        return (
          <span style={{ color: isPositive ? '#2E7D32' : '#C62828', fontWeight: 600 }}>
            {isPositive ? '+' : ''}
            {rawHours.toFixed(2)}
            {currentForList !== undefined && currentForList !== null && (
              <span style={{ color: '#555', fontWeight: 400 }}> ({currentForList})</span>
            )}
          </span>
        );
      },
    },
    {
      field: 'is_garcia',
      headerName: 'Is Garcia',
      flex: 1,
      minWidth: 110,
      valueGetter: (params) => (Number(params?.row?.is_garcia) === 1 ? 'Yes' : 'No'),
    },
    {
      field: 'changed_by',
      headerName: 'Changed By',
      flex: 1,
      minWidth: 200,
      valueGetter: (params) => params?.row?.changed_by?.fullName || '-',
    },
  ];

  return (
    <Paper className="p-4" sx={{ marginTop: '1rem' }}>
      <Typography
        variant="h6"
        sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}
      >
        <h5>Employee Hours Monitor</h5>

        <ButtonTheme color="primary" onClick={() => navigate('/firehouse/reports')}>
          <ArrowBackIosIcon fontSize="small" /> Back
        </ButtonTheme>
      </Typography>

      <Grid container spacing={2} alignItems="flex-end" className="mt-1">
        {/* Employee */}
        <Grid item xs={12} sm={6} md={4}>
          <label className="my-1">Employee</label>
          <Autocomplete
            options={employeeOptions}
            getOptionLabel={(option) => option.label || ''}
            value={employee}
            onChange={(_, newValue) => setEmployee(newValue)}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Select Employee"
                color="secondary"
                sx={{ '& .MuiOutlinedInput-root': { height: '48px', padding: '0 9px' } }}
              />
            )}
          />
        </Grid>

        {/* Start Date */}
        <Grid item xs={12} sm={6} md={2} className="d-grid">
          <label className="my-1">Start Date</label>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={startDate}
              onChange={(e) => setStartDate(e)}
              format="MM/dd/yyyy"
              slotProps={{ textField: { size: 'small', fullWidth: true, placeholder: 'MM/DD/YYYY' } }}
              sx={{ width: '100%', '& .MuiInputBase-root': { height: '48px' } }}
            />
          </LocalizationProvider>
        </Grid>

        {/* End Date */}
        <Grid item xs={12} sm={6} md={2} className="d-grid">
          <label className="my-1">End Date</label>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <DatePicker
              value={endDate}
              onChange={(e) => setEndDate(e)}
              format="MM/dd/yyyy"
              slotProps={{ textField: { size: 'small', fullWidth: true, placeholder: 'MM/DD/YYYY' } }}
              sx={{ width: '100%', '& .MuiInputBase-root': { height: '48px' } }}
            />
          </LocalizationProvider>
        </Grid>

        {/* Search Button */}
        <Grid
          item
          xs={12}
          md={2}
          sx={{ display: 'flex', flexDirection: 'column' }}
          style={{ justifyContent: 'center', marginTop: 'auto', height: '58px', paddingLeft: '12px' }}
        >
          <ButtonTheme color="primary" sx={{ height: '72px', width: '100%' }} onClick={fetchData}>
            Search
          </ButtonTheme>
        </Grid>
      </Grid>

      {loading && (
        <Box className="d-flex justify-content-center mt-5">
          <CircularProgress />
        </Box>
      )}

      {!loading && rows.length === 0 && (
        <Box
          className="d-flex flex-column align-items-center justify-content-center"
          sx={{ height: 300 }}
        >
          <img src="/images/blur-logo.png" height={180} alt="No data" />
          <Typography sx={{ mt: 2, color: '#555' }}>No Data Found</Typography>
        </Box>
      )}

      {!loading && rows.length > 0 && (
        <Box className="mt-3" sx={{ width: '100%' }}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: 1,
              mb: 1,
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1 }}>
              <Typography sx={{ fontWeight: 700 }}>Results</Typography>
              <Typography sx={{ color: '#777', fontSize: 13 }}>{rows.length} records</Typography>
            </Box>

            {currentHours.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                {Object.entries(currentHours[0]?.lists || {}).map(([key, val]) => (
                  <span
                    key={key}
                    style={{
                      backgroundColor: '#F1F1F1',
                      borderRadius: '12px',
                      padding: '4px 12px',
                      fontSize: '12px',
                      fontWeight: 600,
                      color: '#333',
                    }}
                  >
                    {key}: {val}
                  </span>
                ))}
              </Box>
            )}
          </Box>

          <DataGrid
            autoHeight
            rows={rows}
            columns={columns}
            getRowId={(r) => r.id}
            disableRowSelectionOnClick
            pagination
            pageSizeOptions={[10, 25, 50, 100]}
            initialState={{ pagination: { paginationModel: { pageSize: 25, page: 0 } } }}
            sx={{ '& .MuiDataGrid-columnHeaders': { backgroundColor: '#f5f5f5', fontWeight: 700 } }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default EmployeeHoursMonitor;
